<template>
  <main>
    <!-- Update notification banner -->
    <UpdateNotification />

    <div v-if="is_reload" class="local-loading-container">
      <LoadingScreen />
    </div>
    <div class="main-app" v-else>
      <Navbar v-if="showNavbar" />
      <div :class="contentClass">
        <router-view />
      </div>
      <Footer v-if="showFooter" />
      <InstallPrompt />
    </div>
    <!-- Attendance verification popup for request creators -->
    <AttendanceCheckModal v-if="currentAttendanceReport" :show="!!currentAttendanceReport"
      :report="currentAttendanceReport" @close="dismissAttendanceReport" @submitted="nextAttendanceReport" />
  </main>
</template>
<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import Navbar from './components/Navbar.vue';
import Footer from './components/Footer.vue';
import LoadingScreen from './views/LoadingScreen.vue';
import InstallPrompt from './components/InstallPrompt.vue';
import UpdateNotification from './components/UpdateNotification.vue';
import AttendanceCheckModal from './views/my_requests/AttendanceCheckModal.vue';
import { isReportFullyHandled } from './views/my_requests/attendanceLock';
import { states } from './main_store';
import { db, projectAuth, requestFCMToken, checkNotificationSupport } from '@/firebase/config'
import { updateThemeColor, initThemeColor } from './scripts/themeColor'
import { doc, getDoc, setDoc, arrayUnion, collection, query, where, getDocs } from 'firebase/firestore'

// Initialize theme color immediately based on URL (before Vue Router resolves)
initThemeColor()

const is_reload = computed(() => {
  // Show loading until auth state is determined
  return !states.value.authIsReady || states.value.is_reload;
});

// Fetch background image from Firestore
const getBackImg = async () => {
  try {
    const bkcImgRef = doc(db, 'Admin', 'background');
    const bkcImgDoc = await getDoc(bkcImgRef);
    if (bkcImgDoc.exists()) {
      if (bkcImgDoc.data().showImage) {
        states.value.bckImage = bkcImgDoc.data().bckImage;
      } else {
        states.value.bckImage = null;
      }
    }
  } catch (error) {
    // Silently fail if user doesn't have permissions (not logged in)
    console.log('Background image fetch skipped - user not authenticated');
  }
};

const route = useRoute();

// Update theme color immediately when route changes (fixes initial load timing issue)
watch(() => route.name, (routeName) => {
  if (routeName) {
    updateThemeColor(routeName)
  }
}, { immediate: true })

// Token refresh logic - runs when user state changes
const refreshFCMToken = async () => {
  try {
    const authUser = projectAuth.currentUser;
    if (!authUser) return;

    // Check if notifications are supported
    if (!checkNotificationSupport()) return;

    // Check if user has notifications enabled
    const subDocRef = doc(db, 'subscription', authUser.uid);
    const subDocSnap = await getDoc(subDocRef);

    if (!subDocSnap.exists()) return;

    const subData = subDocSnap.data();
    if (!subData.substatus) return; // Notifications not enabled

    // Check if permission is still granted
    if (Notification.permission !== 'granted') return;

    // Get current FCM token
    const currentToken = await requestFCMToken();
    if (!currentToken) return;

    // Check if token is already in the array
    const storedTokens = subData.tokens || [];
    if (!storedTokens.includes(currentToken)) {
      // Add new token to Firestore
      await setDoc(subDocRef, {
        tokens: arrayUnion(currentToken),
        substatus: true
      }, { merge: true });
      console.log('FCM token refreshed and added to Firestore');
    }

    // Update global state
    states.value.notificationToken = currentToken;
    states.value.substatus = true;
  } catch (error) {
    console.error('Error refreshing FCM token:', error);
  }
};

// Attendance verification queue - reports the creator hasn't answered yet
const pendingAttendanceReports = ref([])
const currentAttendanceReport = computed(() => pendingAttendanceReports.value[0] || null)

const checkPendingAttendance = async (uid) => {
  try {
    const reportsQuery = query(
      collection(db, 'attendance-reports'),
      where('creatorId', '==', uid),
      where('status', '==', 'pending')
    );
    const snapshot = await getDocs(reportsQuery);
    pendingAttendanceReports.value = snapshot.docs
      .map(reportDoc => ({ id: reportDoc.id, ...reportDoc.data() }))
      // A coordinator can credit before the requester answers - skip reports with nothing left to ask
      .filter(report => !isReportFullyHandled(report));
  } catch (error) {
    console.error('Failed to fetch pending attendance reports:', error);
  }
};

// "אשיב מאוחר יותר" - hide for this session, re-prompts on next app open
const dismissAttendanceReport = () => {
  pendingAttendanceReports.value = pendingAttendanceReports.value.slice(1);
};

const nextAttendanceReport = () => {
  pendingAttendanceReports.value = pendingAttendanceReports.value.slice(1);
};

// Watch for user authentication state changes
watch(() => states.value.user, (newUser) => {
  if (newUser && newUser.uid) {
    // User is logged in, refresh FCM token and load background image
    refreshFCMToken();
    getBackImg();
    checkPendingAttendance(newUser.uid);
  } else {
    pendingAttendanceReports.value = [];
  }
}, { immediate: true });

// Array of route names where the footer/navbar should be hidden
const pagesWithoutFooter = ['Login', 'Signup', 'Verification', 'PrivacyPolicy'];
const pagesWithoutNavbar = ['Login', 'Signup', 'Verification'];

// Determine whether to show the footer based on the current route
const showFooter = computed(() => {
  return !pagesWithoutFooter.includes(route.name);
});

// Determine whether to show the navbar based on the current route
const showNavbar = computed(() => {
  return !pagesWithoutNavbar.includes(route.name);
});

// Determine content class based on navbar/footer visibility
const contentClass = computed(() => {
  const hasNavbar = showNavbar.value;
  const hasFooter = showFooter.value;

  if (!hasNavbar && !hasFooter) return 'content-fullscreen';
  if (!hasNavbar) return 'content-no-navbar';
  if (!hasFooter) return 'content-no-footer';
  return 'content';
});
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Rubik:wght@300;400;500;600;700;800;900&display=swap');

* {
  font-family: 'Rubik', sans-serif;
  box-sizing: border-box;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}

main {
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  height: 100vh;
}

.main-app {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
}

.content {
  position: relative;
  margin: 0 auto;
  width: 100%;
  max-width: 1200px;
  overflow-y: auto;
  height: calc(100% - 180px);
  padding: 5px;
}

.content-no-footer {
  position: relative;
  margin: 0 auto;
  width: 100%;
  max-width: 1200px;
  overflow-y: auto;
  height: calc(100% - 100px);
  padding: 5px;
}

.content-no-navbar {
  position: relative;
  margin: 0 auto;
  width: 100%;
  max-width: 1200px;
  overflow-y: auto;
  height: calc(100% - 80px);
  padding: 5px;
}

.content-fullscreen {
  position: relative;
  margin: 0 auto;
  width: 100%;
  max-width: 100%;
  overflow-y: auto;
  height: 100%;
  padding: 0;
}

@media only screen and (max-width: 600px) {
  .content {
    height: calc(100% - 130px);
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .content::-webkit-scrollbar {
    display: none;
  }

  .content-no-footer {
    height: calc(100% - 50px);
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .content-no-footer::-webkit-scrollbar {
    display: none;
  }

  .content-no-navbar {
    height: calc(100% - 80px);
    overflow-y: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .content-no-navbar::-webkit-scrollbar {
    display: none;
  }

  .content-fullscreen {
    height: 100%;
  }
}

*::-webkit-scrollbar {
  width: 5px;
  /* Adjust the width as needed */
}

/* Style the scrollbar thumb (the draggable part) */
*::-webkit-scrollbar-thumb {
  background-color: #0059FF;
  /* Background color of the thumb */
  border-radius: 5px;
  /* Rounded corners for the thumb */
}

/* Style the scrollbar track on hover */
*::-webkit-scrollbar-track:hover {
  background-color: var(--secondary);
  /* Background color on hover */
}

/* Style the scrollbar thumb on hover */
*::-webkit-scrollbar-thumb:hover {
  background-color: var(--light-blue);
  /* Thumb color on hover */
}
</style>
